import { equals, isEmpty } from 'ramda';
import { useState } from 'react';
import { useSelectionSort, useBubbleSort, useInsertionSort, useCombSort, useShellSort, useCountingSort,
         useCocktailSort, useGnomeSort, useBrickSort } from '../../hooks/algorithm';
import { calculateColumnWidth, getRandomArray, getArray } from '../useFunctions/useFunctions';
import { stringToArray } from '../../util/inputList';
import { nearlySortedRandomList, reversedNearlySortedRandomList } from '../../util/generator';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import Button from '@material-ui/core/Button';
import './Preview.css';

interface SortFunction {
    ( list: number[], time: number, play: boolean ) : {
        items: number[],
        highlight: number[]
    }
}

interface PreviewProps {
    useSort: SortFunction
}

function withSort(Preview : React.FC<PreviewProps>, useSort : SortFunction) {
    return () => <Preview useSort={ useSort } />
}

function Preview({ useSort } : PreviewProps) {
  let windowWidth = window.innerWidth;
  let max = window.innerHeight * 0.6;

  const [arrayLength, setArrayLength] = useState(40);
  const [arrayWidth, setArrayWidth] = useState(20);
  const [start, setStart] = useState(false);
  const [delay, setDelay] = useState(50);
  const [columnsArray, setColumnsArray] = useState(
    getRandomArray(max, 1, arrayLength)
  );
  const [customArray, setCustomArray] = useState("");
  
  const getNewArray = (newArrayLength:number) => {
    setColumnsArray(getRandomArray(max, 1, newArrayLength));
  }
  
  const getNewNearlySortedArray = (newArrayLength:number) => {
    setColumnsArray(nearlySortedRandomList(max, 1, newArrayLength));
  }

  const getNewReversedNearlySortedArray = (newArrayLength:number) => {
    setColumnsArray(reversedNearlySortedRandomList(max, 1, newArrayLength));
  }

  const { items, highlight } = useSort(columnsArray, delay, start);
  
  const handleDelayChange = (event: any, value: any) => {
    setDelay(value);
  };
  
  const handleStartChange = () => {
    setStart(!start);
  }
  
  const handleArrayLengthChange = (n: number) => {
    if (!start) {
      let newArrayLength = arrayLength + n;
      if (newArrayLength > 10 && newArrayLength < 121) {
        setArrayLength(arrayLength + n);
        setArrayWidth(calculateColumnWidth(windowWidth, newArrayLength));
        getNewArray(newArrayLength);
      }
    }
  }

  const handleCustomArray = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomArray(e.currentTarget.value);
  }

  const handleCreateCustomArray = () => {
    if(isEmpty(customArray.replace(/\D/g, ''))){
      alert("input doesn't contain any numbers");
    } else {
      setColumnsArray(getArray(max, 1, stringToArray(customArray)));
    }
  }

  return (
    <div className="container">
      <div className="flex justify-content-center m-10">
        <div className="preview-box">
          {items.map((height, index) =>
          <div
            style={{height: height, width: arrayWidth}}
            className={"item "+(highlight.some(equals(index)) ? "highlight" : "")}
          />)}
        </div>
      </div>

      <div>
        <div className="flex justify-content-center m-10">
          <Button
            variant="contained"
            className="green-button"
            onClick={handleStartChange}
            disabled={start}
          >
            Start
          </Button>
          <Button
            variant="contained"
            className="red-button"
            onClick={handleStartChange}
            disabled={!start}
          >
            Stop
          </Button>
        </div>
        <div className="justify-content-center m-10">
          <Typography>
            Restart with New List : 
          </Typography>
          <Button
            variant="contained"
            className="restart-button"
            onClick={() => {getNewArray(arrayLength);}}
            disabled={start}
          >
            Pure Random
          </Button>
          <Button
            variant="contained"
            className="restart-button"
            onClick={() => {getNewNearlySortedArray(arrayLength);}}
            disabled={start}
          >
            Nearly Sorted
          </Button>
          <Button
            variant="contained"
            className="restart-button"
            onClick={() => {getNewReversedNearlySortedArray(arrayLength);}}
            disabled={start}
          >
            Nearly Sorted in Reverse
          </Button>
        </div>
        <div>
          <div className="flex justify-content-center m-10">
            <form onSubmit={e => {e.preventDefault();}}>
              <Typography>
                Custom Array : <span>&nbsp;</span>
                <input type="text" onChange={handleCustomArray} />
              </Typography>
            </form>
            <span>&nbsp;&nbsp;</span>
            <Button
            variant="contained"
            color="primary"
            onClick={handleCreateCustomArray}
            disabled={start}
            >
              Apply
            </Button>
          </div>
        </div>

        <div className="flex flex-column justify-content-center m-10">
          <Typography>
            Array Length: 
          </Typography>
          <div>
            <Button
              variant="contained"
              className="change-len-button"
              onClick={() => {handleArrayLengthChange(-10)}}
            >
              -
            </Button>
            <span>{arrayLength}</span>
            <Button
              variant="contained"
              className="change-len-button"
              onClick={() => {handleArrayLengthChange(+10);}}
            >
              +
            </Button>
          </div>
        </div>

        <div className="flex slider-container">
          <Typography 
            id="discrete-slider"
          >
            Delay: 
          </Typography>
          <Slider
            defaultValue={50}
            value={delay}
            aria-labelledby="discrete-slider"
            valueLabelDisplay="auto"
            onChange={handleDelayChange}
            step={5}
            marks
            min={2}
            max={100}
          >
          </Slider>
        </div>
      </div>
    </div>
  )
};

const SelectionPreview = withSort(Preview, useSelectionSort);
const InsertionPreview = withSort(Preview, useInsertionSort);
const BubblePreview    = withSort(Preview, useBubbleSort);
const CombPreview      = withSort(Preview, useCombSort);
const ShellPreview     = withSort(Preview, useShellSort);
const CountingPreview  = withSort(Preview, useCountingSort);
const CocktailPreview  = withSort(Preview, useCocktailSort);
const GnomePreview     = withSort(Preview, useGnomeSort);
const BrickPreview     = withSort(Preview, useBrickSort);

export {
    SelectionPreview,
    InsertionPreview,
    BubblePreview,
    CombPreview,
    ShellPreview,
    CountingPreview,
    CocktailPreview,
    GnomePreview,
    BrickPreview
}
